// Spa Buddy report replicas — maps Aqualux data onto the exact legacy Access report layouts.
import { reportHtml } from "./engine";
import { routePrint } from "../../../../software/printAgent";
import { resolveBrand, registerCompanyInfo } from "../../client/printDocs";
import { api } from "../../../../software/api";

let _companies = [];
let _scripts = null;
/** Loads CompanySetup records and client document scripts once so every print has its header and wording. */
export async function ensureCompanyInfo() {
  await Promise.all([_companies.length ? null : loadCompanies(), _scripts ? null : ensureScripts()]);
}
async function loadCompanies() {
  try {
    const rows = await api("GET", "/company-setup");
    const map = {};
    _companies = (rows || []).filter((c) => c.name).map((c) => {
      const lu = c.logo_url && c.logo_url.startsWith("/") ? `${window.location.origin}${c.logo_url}` : c.logo_url;
      map[c.name.trim().toLowerCase()] = { ...c, logo_url: lu };
      return map[c.name.trim().toLowerCase()];
    });
    registerCompanyInfo(map);
  } catch { /* offline: header falls back to the brand name */ }
}
export async function ensureScripts() {
  try { _scripts = Object.fromEntries((await api("GET", "/scripts")).map((r) => [r.id, r.content])); } catch { /* keep the last loaded wording */ }
}
/** Script text with {Company} / {ClientName} / {Number} placeholders filled. */
export const scriptText = (id, vars = {}) => String((_scripts || {})[id] ?? "").replace(/\r\n/g, "\n").replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] ?? "" : m));
// Scripts that replace fixed labels on the legacy layouts ("Report.Control").
const SCRIPT_TARGETS = {
  "Work Order Quotation GST Note": ["WorkOrderQuotation.Label15"],
  "Invoice GST Note": ["Invoice.Label15"],
  "Invoice Delivery Terms": ["InvoiceSubreport.Label74"],
  "Invoice Refund Heading": ["Invoice.Label5"],
  "Invoice Refund Policy": ["Invoice.Label54"],
  "Invoice Direct Deposit Note": ["Invoice.Label116", "WorkOrderInvoice.Label116"],
  "Statement Remittance Note": ["StatementCompanySetupSubreport.Label34"],
  "Statement Tear-off Note": ["StatementClient.Label122"],
  "Delivery Acknowledgement": ["Delivery.Label5"],
  "POS Receipt Thank You": ["POSReceipt.Text47", "POSServiceReceipt.Text47", "POSReceiptReprint.Text47"],
  "POS Receipt Refund Heading": ["POSReceipt.Text79", "POSServiceReceipt.Text80", "POSReceiptReprint.Text79"],
  "POS Receipt Refund Policy": ["POSReceipt.Text78", "POSServiceReceipt.Text79", "POSReceiptReprint.Text78"],
  "POS Receipt Retain Note": ["POSReceipt.Text48", "POSServiceReceipt.Text48", "POSReceiptReprint.Text48"],
};
function scriptLabels(company) {
  const text = {};
  Object.entries(SCRIPT_TARGETS).forEach(([id, keys]) => {
    if ((_scripts || {})[id] == null) return;
    const t = scriptText(id, { Company: company.CompanyID });
    keys.forEach((k) => { text[k] = t; });
  });
  return text;
}
const scriptRows = (id) => ((_scripts || {})[id] != null ? [{ ScriptsID: id, Content: scriptText(id) }] : []);

const D = (v) => { if (!v) return null; const d = new Date(String(v).length <= 10 ? `${v}T00:00:00` : v); return isNaN(d) ? null : d; };
const nm = (c) => [c?.first_name, c?.surname].filter(Boolean).join(" ");

export function companyRow(brand) {
  let c = resolveBrand(brand) || {};
  if (!c.address && _companies.length) c = _companies[0];
  return {
    CompanyID: c.name || "", Address: c.address || "", Suburb: c.suburb || "", State: c.state || "", PostCode: c.postcode || "", Postcode: c.postcode || "",
    Phone: c.phone || "", Fax: c.fax || "", Email: c.email || "", Website: c.website || "", ACN: c.acn || "", ABN: c.abn || "",
    Bank: c.bank || "", Branch: c.branch || "", AccountName: c.account_name || "", BSB: c.bsb || "", Account: c.account || "",
    TradingHours: c.trading_hours || "", EmailFooter: c.email_footer || "", Logo: c.logo_url || "", logo_url: c.logo_url || "",
  };
}

function billTo(doc, client) {
  const a = client?.address || {};
  return {
    InvoiceTo: doc?.addressed_to || nm(client) || client?.name || doc?.client_name || "", QuoteTo: doc?.addressed_to || nm(client) || client?.name || "",
    Address: doc?.addressed_address || a.street || "", Suburb: a.suburb || "", State: a.state || "", PostCode: a.postcode || "", Postcode: a.postcode || "",
    "InvoiceNumber.Address": a.street || "", "InvoiceNumber.Suburb": a.suburb || "", "InvoiceNumber.State": a.state || "", "InvoiceNumber.InvoiceTo": doc?.addressed_to || client?.name || "",
    FirstName: client?.first_name || "", Surname: client?.surname || client?.name || "", ResidentialStreet: a.street || "", Client: client?.name || "",
  };
}

const itemRows = (items) => (items || []).map((i) => {
  const gross = (i.qty || 0) * (i.unit_price ?? i.unit ?? 0);
  return { Product: i.name || i.item || "", Make: "", Model: "", "Part Number": i.sku || "", Price: i.unit_price ?? i.unit ?? 0, Quantity: i.qty || 0,
    QTY: i.qty || 0, Gross: i.gross ?? gross, Discount: i.discount || 0, Net: i.line_total ?? i.net ?? gross - (i.discount || 0) };
});
const payRows = (pays) => (pays || []).map((p) => ({ DatePayment: D(p.date || p.at), PaymentAmount: p.amount || 0, PaymentMethod: p.method || "" }));
const colourRows = (items) => (items || []).filter((i) => (i.colours || []).length || i.shell_colour || i.cabinet_colour || i.jet_colour || i.cover_colour).map((i) => {
  const c = (k, f) => i[f] || ((i.colours || []).find((x) => x.item === k) || {}).name || "";
  return { "Shell Colour": c("Shell", "shell_colour"), "Cabinet Colour": c("Cabinet", "cabinet_colour"), "Hard Cover Colour": c("Cover", "cover_colour"), "Jet Colour": c("Jet", "jet_colour") };
});

function saleTotals(sale) {
  const items = sale.items || [];
  const gross = items.reduce((a, i) => a + (i.qty || 0) * (i.unit_price || 0), 0);
  const disc = items.reduce((a, i) => a + (i.discount || 0), 0) + (sale.discount || 0);
  const paid = (sale.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
  const nett = sale.total || 0;
  return { Gross: gross, Discount: disc, Nett: nett, BalanceDue: Math.round((nett - paid) * 100) / 100 };
}

const print = (html, title, target = "a4") => routePrint(target, html, title);

/** Report_Invoice / Report_WorkOrderInvoice (and the "Invoice Payment Receipt" attachment). */
export function invoiceReportHtml(sale, client, brand, wo) {
  const company = companyRow(sale.brand || brand);
  const t = saleTotals(sale);
  const row = {
    ...company, ...billTo(sale, client), ...t, InvoiceNumberID: sale.sale_number, InvoiceID: sale.sale_number, QuotationID: sale.legacy_quotation_id || sale.quote_number || "",
    DeliveryDate: D(sale.delivery_date), DateInvoiceSent: D(sale.invoice_sent_date || sale.created_at), SalesPerson: sale.salesperson || sale.employee || "",
    InvoicedBy: company.CompanyID, JNumber: sale.jnumber || "", DeliveryMethod: sale.delivery_method || "", DeliveryNotes: sale.delivery_instructions || sale.delivery_notes || "",
    SaleDate: D(sale.created_at), Text69: t.BalanceDue,
  };
  const items = itemRows(sale.items);
  const subs = {
    ClientInvoiceSubreport: { rows: items }, InvoicePaymentsSubreport: { rows: payRows(sale.payments) }, ClientQuoteShellColour: { rows: colourRows(sale.items) },
    "WorkOrderInvoiceAd subreport": { rows: scriptRows("Work Order Invoice Ad") }, Form_WorkOrderItemsInvoiceReport: { rows: items },
    WorkOrderInvoiceSubform: { rows: wo ? [{ WorkOrderID: wo.wo_number, WorkType: wo.work_type || "Chargeable", WarrantyAuthority: wo.warranty_authority || "",
      WorkOrderDateCompleted: D(wo.date_completed), WorkOrderCarriedOutBy: wo.technician || "", DescribeResult: wo.result_text || "" }] : [] },
  };
  const isWo = !!(sale.wo_id || sale.wo_number);
  const name = isWo ? "WorkOrderInvoice" : "Invoice";
  const text = { ...scriptLabels(company), ...(company.logo_url ? { "WorkOrderInvoice.InvoicedBy": "" } : {}) }; // Access hides it behind the logo
  const globals = { company, text, refs: { Client: { SelectWorkOrder: sale.wo_number || wo?.wo_number || "", SelectInvoice: sale.sale_number } } };
  return reportHtml(name, { rows: [row], subs }, globals, { title: `Invoice ${sale.sale_number}` });
}
export const printLegacyInvoice = (sale, client, brand, wo) => print(invoiceReportHtml(sale, client, brand, wo), `Invoice ${sale.sale_number}`);

/** Report_ClientQuote (spa/retail quote). */
export function printLegacyClientQuote(quote, client, brand) {
  const company = companyRow(quote.brand || brand);
  const items = itemRows(quote.items);
  const row = { ...company, ...billTo(quote, client), QuoteNumberID: quote.quote_number, QuoteNumber: quote.quote_number, QuoteDate: D(quote.created_at), SalesPerson: quote.salesperson || quote.created_by || "" };
  const subs = { ClientQuoteSubreport: { rows: items }, QuoteName: { rows: [billTo(quote, client)] }, ClientSpasQuerySubreport2: { rows: [] }, ClientQuote2subreport: { rows: [row] },
    Form_QuoteValidTo: { rows: scriptRows("Quotation Valid") }, Form_SpaQuotationFooter: { rows: scriptRows("Spa Quotation Footer") } };
  const split = (quote.items || []).map((i) => i.colour_name).find(Boolean);
  const parts = split ? split.split(",").map((x) => x.replace(/\s*\+\s*\$[\d.,]+/, "").trim()) : [];
  const cols = colourRows(quote.items)[0] || { "Shell Colour": parts[0] || "", "Cabinet Colour": parts[1] || "", "Hard Cover Colour": "", "Jet Colour": parts[2] || "" };
  const globals = { company, text: scriptLabels(company), refs: { ClientQuote: { ClientSpasQuerySubreport2: { Options2Subreport: cols } } } };
  return print(reportHtml("ClientQuote", { rows: [row], subs }, globals, { title: `Quote ${quote.quote_number}` }), `Quote ${quote.quote_number}`);
}

/** Report_WorkOrderQuotation (quote raised from a work order). */
export function printLegacyWorkOrderQuote(wo, client, brand, rows, me) {
  const company = companyRow(wo.invoiced_by || brand);
  const items = itemRows((rows || wo.lines || []).map((l) => ({ name: l.item || l.name, qty: l.qty, unit_price: l.unit ?? l.unit_price, discount: l.discount, line_total: l.net ?? l.line_total, gross: l.gross })));
  const row = { ...company, ...billTo(wo, client), QuoteDate: D(wo.quote_date || wo.created_at), WorkOrderID: wo.wo_number, WorkType: wo.work_type || "Chargeable",
    WarrantyAuthority: wo.warranty_authority || "", WorkOrderDateBooked: D(wo.date_booked || wo.created_at), WorkOrderTakenBy: wo.booked_by || "", DescribeIssue: wo.issue_text || "" };
  const subs = { Form_WorkOrderItemsQuoteReport2: { rows: items }, "WorkOrderQuotationMessage subreport": { rows: scriptRows("Work Order Quotation Message") }, "WorkOrderQuotationAd subreport": { rows: scriptRows("Work Order Quotation Ad") } };
  const globals = { company, text: scriptLabels(company), refs: { Client: { SelectQuote: wo.quote_number || wo.wo_number, SelectWorkOrder: wo.wo_number }, Switchboard: { Employee: me || wo.booked_by || "" } } };
  return print(reportHtml("WorkOrderQuotation", { rows: [row], subs }, globals, { title: `W/O Quote ${wo.wo_number}` }), `Quote ${wo.wo_number}`);
}

/** Report_StatementClient (30/60/90 day aged statement). */
export function printLegacyStatement(client, sales, brand) {
  const company = companyRow(brand);
  const open = (sales || []).filter((s) => !/refunded|cancel/i.test(s.status || "")).map((s) => {
    const nett = s.total || 0;
    const bal = s._legacy ? (typeof s.balance_due === "number" ? s.balance_due : 0) : nett - (s.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
    return { InvoiceNumberID: s.sale_number, DateInvoiceSent: D(s.invoice_sent_date || s.created_at), InvoiceTo: s.addressed_to || client?.name || "", Client: client?.name || "",
      "InvoiceNumber.Suburb": client?.address?.suburb || "", Nett: nett, BalanceDue: Math.round(bal * 100) / 100, WarrantyAuthority: s.warranty_authority || "" };
  }).filter((r) => Math.abs(r.BalanceDue) > 0.005);
  const to = billTo({}, client);
  const subs = { StatementQuerySubreport: { rows: open }, StatementInvoiceTo: { rows: [to] }, StatementInvoiceTo2: { rows: [to] } };
  return print(reportHtml("StatementClient", { rows: [{ ...company, ...to }], subs }, { company, text: scriptLabels(company) }, { title: `Statement ${client?.name || ""}` }), "Statement");
}

/** Report_Delivery (delivery instruction docket). */
export function printLegacyDelivery(sale, client, brand) {
  const company = companyRow(sale.brand || brand);
  const to = billTo(sale, client);
  const phones = (client?.contact_numbers || []).map((n) => ({ ContactType: n.phone_type || "", Field1: n.number || "" }));
  const row = { ...company, ...to, InvoiceNumberID: sale.sale_number, QuoteNumber: sale.legacy_quotation_id || "" };
  const subs = {
    DeliveryNamesSubreport: { rows: [{ ...to, NameID: 1 }], subs: { DeliveryClientContactNumbers: { rows: phones } } },
    DeliveryQuoteSubreport: { rows: itemRows(sale.items) }, ClientQuote2subreport: { rows: [{ SalesPerson: sale.salesperson || sale.employee || "" }] },
    DeliverySubreport: { rows: [{ InvoiceNumberID: sale.sale_number, QuotationID: sale.legacy_quotation_id || "", DeliveryDate: D(sale.delivery_date), DeliveryNotes: sale.delivery_instructions || sale.delivery_notes || "", JNumber: sale.jnumber || "" }] },
    DeliveryClientSpasQuery: { rows: [] }, DeliveryClientGazeboQuery: { rows: [] }, DeliveryClientSaunaQuery: { rows: [] }, "BusinessContacts subreport": { rows: [] },
    InvoiceSubreport: { rows: [{ InvoiceNumberID: sale.sale_number }] },
  };
  return print(reportHtml("Delivery", { rows: [row], subs }, { company, text: scriptLabels(company) }, { title: `Delivery ${sale.sale_number}` }), "Delivery");
}

/** Report_POSReceipt — 80mm receipt printer. */
export function posReceiptHtml(sale, brand, reprint) {
  const company = companyRow(brand);
  const by = (re) => (sale.payments || []).filter((p) => re.test(p.method || "")).reduce((a, p) => a + (p.amount || 0), 0);
  const at = D(sale.created_at) || new Date();
  const pos = {
    POSID: sale.sale_number, POSDate: at, POSTime: at, POSEmployee: sale.clerk || sale.employee || "", POSSaleType: sale.client_id ? "Client Counter Sale" : "Counter Sale",
    POSInvoiceTo: sale.client_name || "", POSClientName: sale.client_name || "", POSInvoiceID: sale.sale_number, POSAmount: sale.total || 0, POSQuoteAmount: sale.total || 0,
    ThisSale: sale.total || 0, POSRounding: sale.rounding || 0, AmountTendered: sale.amount_tendered || 0, POSAmountTendered: sale.amount_tendered || 0, Change: sale.change || 0, POSChange: sale.change || 0,
    POSCashAmount: by(/cash/i), POSEftposAmount: by(/eftpos|card/i), POSEftpos: by(/eftpos|card/i), POSVisaAmount: by(/visa/i), POSMastercardAmount: by(/master/i), POSAmexAmount: by(/amex/i), POSChequeAmount: by(/cheque/i),
  };
  const items = itemRows(sale.items).map((r) => ({ ...r, Active: true }));
  const name = reprint ? "POSReceiptReprint" : "POSReceipt";
  const subs = { Form_POSReceiptItems: { rows: items }, Form_POSReceiptItemsReprint: { rows: items }, Form_LogonCompanyDetails: { rows: [company] }, Form_LogonCompanyDetails2: { rows: [company] } };
  return reportHtml(name, { rows: [pos], subs }, { company, text: scriptLabels(company), refs: { POS: pos } }, { paper: "receipt", title: `Receipt ${sale.sale_number}` });
}
export const printPosReceipt = (sale, brand, reprint) => print(posReceiptHtml(sale, brand, reprint), `Receipt ${sale.sale_number}`, "receipt");

/** Report_POSStartOfDayOpenTill — till float docket (80mm). */
export function printStartOfDayDocket(clerk, float, brand) {
  const company = companyRow(brand);
  const globals = { company, refs: { Switchboard: { Employee: clerk }, CashDrawer: { CashDrawerFloat: float } } };
  return print(reportHtml("POSStartOfDayOpenTill", { rows: [{ ...company, CashDrawerFloat: float, Employee: clerk }] }, globals, { paper: "receipt", title: "Start of Day" }), "Start of Day", "receipt");
}

/** Report_POSEndOfDayOpenTill — the "throw in the bin" docket that kicks the till drawer open (80mm). */
export function printEndOfDayOpenTill(brand) {
  const company = companyRow(brand);
  return print(reportHtml("POSEndOfDayOpenTill", { rows: [{ ...company }] }, { company }, { paper: "receipt", title: "Open Till" }), "Open Till", "receipt");
}

/** Report_POSEndOfDay — POS summary (80mm) from GET /pos/end-of-day rows. */
export function printPosEndOfDay(data, employee, brand, viewOnly) {
  const company = companyRow(brand);
  const pos = (data.pos || []).map((r) => ({ ...r, POSDate: D(r.day) }));
  const nz = (f) => ({ rows: pos.filter((r) => Math.abs(r[f] || 0) > 0.0001) });
  const type = (t) => ({ rows: pos.filter((r) => r.POSSaleType === t) });
  const subs = {
    Form_LogonCompanyDetails: { rows: [company] }, Form_POSRevenueToday: { rows: pos },
    Form_POSCashDrawerReport: { rows: (data.cash_drawer || []).map((r) => ({ ...r, CashDrawerDate: D(r.day) })) },
    Form_POSPettyCashReport: { rows: (data.petty || []).map((r) => ({ ...r, PettyCashDate: D(r.day) })) },
    Form_CountPOSCash: nz("POSCashAmount"), Form_CountPOSEftpos: nz("POSEftpos"), Form_CountPOSVisa: nz("POSVisaAmount"),
    Form_CountPOSMastercard: nz("POSMastercardAmount"), Form_CountPOSAmex: nz("POSAmexAmount"), Form_CountPOSCheque: nz("POSChequeAmount"),
    Form_CountPOSRefund: { rows: pos.filter((r) => r.POSDiscount > 0) },
    Form_CountPOSRefundCash: { rows: pos.filter((r) => r.POSDiscount > 0 && /^cash$/i.test(r.POSPaymentMethod || "")) },
    Form_CountPOSCounterSales: type("POS Counter Sale"), Form_CountClientCounterSales: type("Client Counter Sale"),
    Form_CountPOSServicePayments: type("Service Payment"), Form_CountPOSSpaPayments: type("Spa Payment"),
  };
  const globals = { company, refs: { Switchboard: { Employee: employee || "" }, POSSelection: { StartDate: D(data.start), EndDate: D(data.end) } } };
  const html = reportHtml("POSEndOfDay", { rows: [{ ...company }], subs }, globals, { paper: "receipt", title: "POS End of Day" });
  if (!viewOnly) return print(html, "POS End of Day", "receipt");
  const w = window.open("", "_blank"); // View = print preview only
  if (w) { w.document.write(html.replace(/<script>[\s\S]*?<\/script>/, "")); w.document.close(); }
  return false;
}

/** Report_POSCashDrawerReceipt — petty cash taken out docket (80mm). */
export function printPettyCashDocket(amount, reason, takenBy, brand) {
  const company = companyRow(brand);
  const globals = { company, refs: { Switchboard: { Employee: takenBy || "" }, POSCashDrawer: { AmountTaken: amount, Reason: reason || "" } } };
  return print(reportHtml("POSCashDrawerReceipt", { rows: [{ ...company }], subs: { Form_LogonCompanyDetails: { rows: [company] } } }, globals, { paper: "receipt", title: "Petty Cash" }), "Petty Cash", "receipt");
}

/** Report_POSCashDrawerMoneyBack — "receipt and change back in the drawer" docket (80mm). */
export function printPettyCashBackDocket(brand) {
  const company = companyRow(brand);
  return print(reportHtml("POSCashDrawerMoneyBack", { rows: [{ ...company }] }, { company }, { paper: "receipt", title: "Petty Cash Back" }), "Petty Cash Back", "receipt");
}
